import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { clearRememberMePreference } from '@/lib/authStorage';
import { Capacitor } from '@capacitor/core';
import { isRevenueCatConfigured, initializeRevenueCat } from '@/lib/revenueCatInit';
import * as analytics from '@/lib/analytics';
import { setSingularUserId, unsetSingularUserId } from '@/lib/singular';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signingOut: boolean;
  signOut: () => Promise<void>;
  syncRevenueCatUser: (userId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);

  // RevenueCat sync function for native platforms
  const syncRevenueCatUser = useCallback(async (userId: string) => {
    if (!Capacitor.isNativePlatform()) {
      console.log('AuthContext: Skipping RevenueCat sync - not on native platform');
      return;
    }
    
    try {
      console.log('AuthContext: Syncing user with RevenueCat', userId);
      
      // Ensure SDK is configured before trying to use it
      if (!isRevenueCatConfigured()) {
        console.log('AuthContext: Waiting for RevenueCat SDK to configure...');
        const success = await initializeRevenueCat();
        if (!success) {
          console.warn('AuthContext: RevenueCat SDK failed to configure, skipping sync');
          return;
        }
      }
      
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      await Purchases.logIn({ appUserID: userId });
      console.log('AuthContext: RevenueCat user synced successfully', userId);
    } catch (err) {
      console.error('AuthContext: Failed to sync RevenueCat user', err);
    }
  }, []);

  // RevenueCat logout function for native platforms
  const logoutRevenueCat = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;
    
    try {
      console.log('AuthContext: Logging out from RevenueCat');
      
      // Only try to logout if SDK is configured
      if (!isRevenueCatConfigured()) {
        console.warn('AuthContext: RevenueCat SDK not configured, skipping logout');
        return;
      }
      
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      await Purchases.logOut();
      console.log('AuthContext: RevenueCat user logged out successfully');
    } catch (err) {
      console.error('AuthContext: Failed to logout from RevenueCat', err);
    }
  }, []);

  useEffect(() => {
    // Check if user has a pending deletion request (only after successful auth)
    const checkDeletionStatus = async (userId: string) => {
      const { data, error } = await supabase
        .from('data_deletion_requests')
        .select('status')
        .eq('user_id', userId)
        .in('status', ['pending', 'processing'])
        .maybeSingle();

      if (!error && data) {
        // User has a pending deletion request, sign them out
        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
        // Note: The consuming component should handle the redirect via React Router
        // We set a flag in sessionStorage that the landing page can check
        sessionStorage.setItem('deletion_pending', 'true');
        return true;
      }
      return false;
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle PASSWORD_RECOVERY event — set flag so Auth page and App redirect logic can detect it
        if (event === 'PASSWORD_RECOVERY' && window.location.pathname.startsWith('/partner')) return;
        if (event === 'PASSWORD_RECOVERY') {
          void supabase.functions.invoke('log-security-event', { body: { event: 'password_recovery' } }).catch(() => {});
          sessionStorage.setItem('password_recovery_pending', 'true');
          // Navigate to auth page with recovery param
          window.location.href = '/auth?type=recovery';
          return;
        }
        
        if (session?.user) {
          analytics.identify(session.user.id);
          setSingularUserId(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          analytics.reset();
          unsetSingularUserId();
        }

        // Only check deletion status after successful sign-in (not during token refresh or other events)
        if (session?.user && event === 'SIGNED_IN') {
          // Security log (IP read server-side; deduped there). Never blocks sign-in.
          void supabase.functions.invoke('log-security-event', { body: { event: 'sign_in' } }).catch(() => {});
          // Sync user with RevenueCat on native platforms
          syncRevenueCatUser(session.user.id);

          // Check deletion status and subscription in background (non-blocking)
          setTimeout(() => {
            checkDeletionStatus(session.user.id).then(hasPendingDeletion => {
              if (!hasPendingDeletion) {
                // Invoke subscription check in background without blocking UI
                supabase.functions.invoke('check-subscription').catch((error) => {
                  console.error('Error checking subscription:', error);
                });
              }
            });
            supabase
              .from('profiles')
              .select('analytics_opt_out')
              .eq('user_id', session.user.id)
              .maybeSingle()
              .then(({ data }) => {
                if (data?.analytics_opt_out) analytics.optOut();
              });
          }, 0);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        // Sync user with RevenueCat on native platforms
        syncRevenueCatUser(session.user.id);
        analytics.identify(session.user.id);
        setSingularUserId(session.user.id);

        // Check deletion status and subscription in background (non-blocking)
        setTimeout(() => {
          checkDeletionStatus(session.user.id).then(hasPendingDeletion => {
            if (!hasPendingDeletion) {
              // Invoke subscription check in background without blocking UI
              supabase.functions.invoke('check-subscription').catch((error) => {
                console.error('Error checking subscription:', error);
              });
            }
          });
          supabase
            .from('profiles')
            .select('analytics_opt_out')
            .eq('user_id', session.user.id)
            .maybeSingle()
            .then(({ data }) => {
              if (data?.analytics_opt_out) analytics.optOut();
            });
        }, 0);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    // Set signing out flag and clear state immediately
    setSigningOut(true);
    setUser(null);
    setSession(null);
    
    // Clear remember me preference
    clearRememberMePreference();

    // Clear biometric login credentials on native platforms
    if (Capacitor.isNativePlatform()) {
      try {
        const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
        await NativeBiometric.deleteCredentials({ server: 'com.vent.app' });
        console.log('[AuthContext] Biometric login credentials cleared on sign-out');
      } catch {
        // Ignore errors
      }
    }
    
    // Logout from RevenueCat on native platforms
    await logoutRevenueCat();
    
    // Clean up auth session
    await supabase.auth.signOut().catch(console.error);
    
    // Reset signing out flag - let the consuming component handle navigation
    setSigningOut(false);
  };

  const value = {
    user,
    session,
    loading,
    signingOut,
    signOut,
    syncRevenueCatUser,
  };

  // Removed LoadingAnimation during signOut to prevent spinner flash
  // The consuming component should handle showing loading state if needed
  // and navigate via React Router after signOut completes

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
